import { useState, useRef } from "react";
import Link from "next/link";
import getConfig from "next/config";
import Heading from "@components/Heading";
import Price from "@components/Price";
import AddToCart from "@containers/Catalog/Actions/AddToCart";
import ArrowDown from "@components/Icons/ArrowDown";

import { resizeImage, scrollElementIntoView } from "@lib/helpers";
import ProductBenefits from "@components/ProductPage/ProductBenefits";

const {
  publicRuntimeConfig: { settings },
} = getConfig();

const INITIAL_VISIBLE_OFFERS = 3;

const ProductOffers = ({
  product,
  offers,
  getIsInSpecialCampaign,
  getLabelsBySellerId,
}) => {
  const [visibleOffersLimit, setVisibleOffersLimit] = useState(
    INITIAL_VISIBLE_OFFERS
  );

  const containerEl = useRef(null);
  const offersNumber = offers.length;

  const showMore = (event) => {
    event.preventDefault();

    // Show 6
    if (visibleOffersLimit === INITIAL_VISIBLE_OFFERS) {
      return setVisibleOffersLimit(offersNumber > 6 ? 6 : offersNumber);
    }

    // Show 10
    if (visibleOffersLimit === 6) {
      return setVisibleOffersLimit(offersNumber > 10 ? 10 : offersNumber);
    }

    // Show all
    return setVisibleOffersLimit(offersNumber);
  };

  const collapse = (event) => {
    event.preventDefault();
    setVisibleOffersLimit(INITIAL_VISIBLE_OFFERS);

    setTimeout(() => {
      scrollElementIntoView(containerEl.current, "smooth");
    }, 50);
  };

  return (
    <div className="" ref={containerEl}>
      <div className="mb-5 flow-root">
        <div className="flex text-xl m-0 mb-1">
          <Heading
            as="h2"
            display="h2"
            title="Alte oferte ale acestui produs"
            border="bottom"
            classesProp="mb-4"
          />
        </div>
      </div>

      <ul
        className={`
            m-0 p-0 border-gray-1100 md:border md:rounded-tr-xl md:rounded-bl-xl
            ${
              product.resealed.length <= offersNumber
                ? "mb-22px lg:mb-52px"
                : "mb-23px"
            }
          `}
      >
        {offers
          .filter((offer, index) => visibleOffersLimit >= index + 1)
          .map((offer, index) => {
            const isInSpecialCampaign = getIsInSpecialCampaign(
              offer.seller.id,
              product
            );
            const labels = getLabelsBySellerId(offer.seller.id, product);
            const hasBenefits = labels?.length > 0;
            return (
              <li
                key={index}
                className="p-4 lg:p-20px md:flex justify-between items-center resealed border-gray-1100 border md:border-0 rounded-tr-xl rounded-bl-xl md:rounded-none mt-4 md:mt-0"
              >
                <div className="lg:w-7/12 flex items-center">
                  <div className="mr-8">
                    <img
                      src={resizeImage(product.thumbnail, "300x300")}
                      alt={product.name}
                      className="w-32"
                    />
                  </div>

                  <div className="">
                    Vandut si livrat de <br className="block lg:hidden" />
                    <Link href={`/vendor/${offer.seller.url_key}/`}>
                      <a className="underline text-bleu font-medium">
                        {offer.seller.name}
                      </a>
                    </Link>
                    {isInSpecialCampaign && (
                      <div className="my-2">
                        <img
                          src={`${settings.websiteStatics}/images/oferta-de-black-friday.svg`}
                          alt="oferta de black friday"
                          width={80}
                        />
                      </div>
                    )}
                    {hasBenefits && (
                      <div className="my-2 hidden xs:block">
                        <ProductBenefits benefits={labels} />
                      </div>
                    )}
                  </div>
                </div>

                {hasBenefits && (
                  <div className="my-2 xs:hidden">
                    <ProductBenefits benefits={labels} />
                  </div>
                )}

                <div className="lg:w-5/12 flex lg:flex-wrap items-center justify-between md:justify-end">
                  <Price
                    price={offer.price.selling_price}
                    regularPrice={offer.price.base_price}
                    formatted={true}
                  />

                  <div className="xl:w-232px flex flex-row-reverse sm:block md:ml-8 lg:ml-20">
                    <div className="ml-4 sm:ml-0 mb-0 sm:mb-4">
                      <AddToCart
                        iconOnlyOnMobile
                        product={{
                          mainOffer: 0,
                          offers: [offer],
                          name: product.name,
                          sku: product.sku,
                          image: product.image,
                        }}
                      />
                    </div>
                  </div>
                </div>
              </li>
            );
          })}
      </ul>

      {(offersNumber > visibleOffersLimit ||
        (visibleOffersLimit === offersNumber &&
          offersNumber > INITIAL_VISIBLE_OFFERS)) && (
        <div className="flex justify-center mt-2 md:mt-0">
          <a
            href="#"
            className="flex items-center text-red-brand hover:text-bleu-hover font-medium text-sm"
            onClick={visibleOffersLimit === offersNumber ? collapse : showMore}
          >
            {visibleOffersLimit === offersNumber
              ? `Restrange lista`
              : `Vezi mai multe oferte`}

            <span className="ml-2">
              <ArrowDown
                customClassName={`duration-75 ${
                  visibleOffersLimit === offersNumber ? "rotate-180" : ""
                }`}
              />
            </span>
          </a>
        </div>
      )}
    </div>
  );
};

export default ProductOffers;
